<script>
import { mapGetters } from 'vuex'
import CalendarResource from '@/resources/calendar'
import CalendarTypeResource from '@/resources/calendartype'
import Checkout from '../components/Checkout.vue'
import CalendarReservationsSimple from '../components/CalendarReservationsSimple.vue'
import LoadingReservations from '../components/LoadingReservations.vue'
import WaiverModal from '../components/WaiverModal.vue'
import ReservationRacersForm from '../components/ReservationRacersForm.vue'
import messages from '@/mixins/messages'
import HeaderImage from '../components/HeaderImage.vue'
import ApexClubPitch from '../components/ApexClubPitch.vue'
import DateStepper from '../components/DateStepper.vue'
import { TIMEZONE } from '@/config'
import { timeDateFormattedPretty, dateFormattedPretty } from '@/util'

export default {
    name: 'ReservationsStart',
    mixins: [messages],
    components: {
        CalendarReservationsSimple,
        LoadingReservations,
        ApexClubPitch,
        Checkout,
        HeaderImage,
        WaiverModal,
        ReservationRacersForm,
        DateStepper,
    },
    computed: {
        ...mapGetters(['isApexClub', 'creditsBalance', 'locationResources', 'locationNearest', 'userResource']),
        multiSimBooking() {
            return !!(this.lastCommitData && this.lastCommitData.reservation_ids && this.lastCommitData.reservation_ids.length > 1)
        },
        filters() {
            return {
                type__in: Object.values(this.selectedCalendarTypeResources.map((resource) => resource.id)).join(','),
                active: true,
                location: this.selectedLocation.id,
            }
        },
        selectedReservations() {
            if (Object.keys(this.selectedReservationsMap).length < 1) {
                return []
            }

            return Object.values(this.selectedReservationsMap).reduce((left, right) => left.concat(right))
        },
        distinctCalendarCount() {
            return Object.keys(this.selectedReservationsMap).length
        },
        calendarWrapperClasses() {
            if (this.calendarForceVisible) {
                return 'd-block d-sm-none'
            } else {
                return 'd-none d-lg-block'
            }
        },
    },
    watch: {
        async selectedCalendarTypeResources() {
            await this.search()
        },
        useSpecificTime() {
            this.selectedDate = this.useSpecificTime ? this.specificTime : this.today
            this.search()
        },
        selectedDate() {
            setTimeout(() => this.$refs.swipeToSee.scrollIntoView({ behavior: 'smooth' }), 1e3)
        },
    },
    data() {
        const now = new Date()
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        const maxDate = new Date(now.getFullYear(), now.getMonth() + 2, now.getDate())
        // const today = new Date()
        // const maxDate = moment(today).add(1, 'month').toDate()
        const specificTime = this.$route.query.time ? new Date(this.$route.query.time) : null

        return {
            now,
            today,
            specificTime,
            useSpecificTime: !!specificTime,
            loading: true,
            bookModalSize: 'lg',
            bookingErrorDetails: '',
            lastCommitData: null,
            completing: false,
            filtersExpanded: false,
            selectedDate: specificTime || today,
            selectedReservationsMap: {},
            selectedCalendarTypeResources: [],
            selectedLocation: null,
            dateMin: today,
            dateMax: maxDate,
            calendarForceVisible: false,
            calendarTypeResources: [],
            calendarResources: [],
            locationModalVisible: false,
            showApexClubAlert: false,
        }
    },
    methods: {
        timeDateFormattedPretty,
        dateFormattedPretty,
        async search() {
            this.loading = true
            this.calendarResources = await CalendarResource.filterAll(this.filters, { resolveRelated: true })
            this.selectedReservationsMap = {}
            this.loading = false
        },
        onCalendarSelect(calendar, reservations) {
            // This looks weird because of Vue's computed properties lifecycle
            let newMap = this.selectedReservationsMap
            this.selectedReservationsMap = {}

            if (reservations.length > 0) {
                newMap[calendar.id] = reservations
            } else {
                delete newMap[calendar.id]
            }

            this.selectedReservationsMap = newMap
        },
        forceCalendarOpen() {
            this.calendarForceVisible = true
            window.scroll(0, 0)
        },
        openReservationsModal() {
            this.$refs.bookModal.show()
        },
        closeReservationsModal() {
            this.$refs.bookModal.hide()
            setTimeout(() => (this.selectedReservationsMap = {}), 1000)
            this.$refs.calendarComponents.forEach((component) => component.refresh())
        },
        onBookingCreate(commitData) {
            this.closeReservationsModal()
            this.$analytics.track('created_reservation')
            this.lastCommitData = commitData

            if (this.$refs.waiverModal.hasOutstanding()) {
                this.$refs.waiverModal.next()
            } else if (commitData.redirect) {
                window.location.href = commitData.redirect
            } else if (this.selectedLocation?.attributes.post_booking_url) {
                window.location.href = this.selectedLocation.attributes.post_booking_url
            } else {
                this.$refs.completeModal.show()
            }
        },
        onBookingError(message) {
            this.closeReservationsModal()
            this.bookingErrorDetails = message
            this.$refs.errorModal.show()
        },
        onBookAnother() {
            window.location.reload()
        },
        async completeBooking() {
            this.completing = true
            if (this.$refs.racersForm) {
                const ok = await this.$refs.racersForm.run()
                if (!ok) {
                    this.completing = false
                    return
                }
            }
            this.onBookAnother()
        },
        getCalendarComponentsAvailableForSlot(slot) {
            return this.$refs.calendarComponents.filter((component) => {
                return component.calendarIsAvailableForSlot(slot)
            })
        },
        selectLocation(locationResource) {
            this.selectedLocation = locationResource
            this.locationModalVisible = false

            const newRoute = { ...this.$route, query: { location: locationResource.id } }
            this.$router.replace(newRoute)
            this.search()
        },
    },
    async mounted() {
        if (this.$route.query.location) {
            this.selectedLocation = this.locationResources.find((location) => location.id == this.$route.query.location)
        } else if (!this.selectedLocation) {
            if (this.locationNearest) {
                const location = this.locationResources.find((location) => location.id == this.locationNearest.id)
                this.$nextTick(() => this.selectLocation(location))
            } else {
                this.selectLocation(this.locationResources[0])
            }
        }

        this.calendarTypeResources = await CalendarTypeResource.all({ resolveRelated: true })
        this.selectedCalendarTypeResources = this.calendarTypeResources
        this.loading = false
        setTimeout(() => (this.showApexClubAlert = true), 1000)
    },
}
</script>

<template>
    <div>
        <header-image kicker="Reservar un simulador">
            <h1>Reservas</h1>
        </header-image>
        <b-container class="mt-3 mb-5" fluid>
            <b-row>
                <b-col md="4" xl="3" class="mb-3">
                    <b-card bg-variant="light" :class="calendarWrapperClasses">
                        <date-stepper class="d-block" :value="selectedDate" :min="dateMin" :max="dateMax" ref="calendar" @input="(v) => (selectedDate = v)" />
                        <hr />
                        <b-button variant="primary" @click="openReservationsModal()" :disabled="selectedReservations.length < 1" class="col-sm-12 mb-2">Finalizar compra</b-button>
                        <h5 v-if="locationResources.length > 1">
                            <b-button variant="outline-primary" class="col-sm-12" @click="locationModalVisible = true"
                                >Ubicación: <strong>{{ selectedLocation }}</strong></b-button
                            >
                        </h5>
                        <h5>
                            <b-button variant="outline-primary" class="col-sm-12" @click="filtersExpanded = !filtersExpanded">Filtros de búsqueda ({{ calendarTypeResources.length }}) <b-icon :icon="filtersExpanded ? 'arrows-collapse' : 'arrows-expand'" /></b-button>
                        </h5>
                        <b-collapse id="filters" v-model="filtersExpanded">
                            <b-list-group>
                                <b-list-group-item v-for="(calType, i) in calendarTypeResources" :key="i">
                                    <b-form-checkbox switch :value="calType" v-model="selectedCalendarTypeResources">{{ calType }}</b-form-checkbox>
                                </b-list-group-item>
                                <b-list-group-item v-if="useSpecificTime" class="d-flex justify-content-between align-items-center">
                                    {{ timeDateFormattedPretty(selectedDate) }}
                                    <b-badge pill href="javascript:{}" @click="useSpecificTime = false">Eliminar</b-badge>
                                </b-list-group-item>
                            </b-list-group>
                        </b-collapse>
                        <div v-if="isApexClub">
                            <hr />
                            <b-row>
                                <b-col>
                                    <strong class="text-success"><b-icon icon="patch-check-fill" /> Perteneces a Apex Club</strong>
                                </b-col>
                            </b-row>
                        </div>
                    </b-card>

                    <b-alert v-if="!calendarForceVisible" show variant="primary" class="d-block d-sm-none">
                        <strong>Fecha seleccionada:</strong> {{ dateFormattedPretty(selectedDate) }} (<a href="javascript:{}" @click="forceCalendarOpen()">cambiar</a>)<br />
                        <strong>Ubicación seleccionada:</strong> {{ selectedLocation }}<br />
                        <a href="javascript:{}" @click="forceCalendarOpen()">Mostrar más filtros</a>
                    </b-alert>

                    <b-alert variant="info" fade :show="showApexClubAlert && !isApexClub && $appConfigBool('APEX_CLUB_ENABLED', true)" class="mt-3"
                        ><strong>Ahorra un 20 % en cada carrera</strong> al unirte a <strong>{{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }}</strong
                        >. (¡Y mucho más!) <a href="javascript:{}" @click="$refs.apexClubModal.show()">Más información</a></b-alert
                    >

                    <div class="d-block d-sm-none" ref="swipeToSee">
                        <hr />
                        <h3 class="d-block d-sm-none">Simuladores disponibles:</h3>
                        <h5 class="text-muted">Desliza para ver más <b-icon icon="arrow-right" animation="cylon" /></h5>
                    </div>
                </b-col>
                <b-col v-if="loading">
                    <loading-reservations />
                </b-col>
                <b-col v-else class="calendars">
                    <div :style="`width: ${this.calendarResources.length * 280}px`">
                        <b-card class="calendar mr-3" v-for="(calendar, i) in calendarResources" :key="i" :img-src="calendar.get('image') ? String(calendar.get('image')) : String(calendar.get('type.image'))">
                            <calendar-reservations-simple :calendar-id="calendar.id" :for-date="selectedDate" ref="calendarComponents" @input="(reservations) => onCalendarSelect(calendar, reservations)" many>
                                <template slot="calendar-body" slot-scope="data">
                                    <b-button variant="primary" size="sm" block @click="forceCalendarOpen()" v-if="data.reservations.length == 0" class="d-block d-sm-none">Cambiar la fecha seleccionada</b-button>
                                </template>
                            </calendar-reservations-simple>
                        </b-card>
                    </div>
                </b-col>
            </b-row>

            <!-- Modals -->
            <b-modal ref="bookModal" :size="bookModalSize" title="Finalizar compra" hide-footer no-fade centered>
                <checkout :reservation-resources="selectedReservations" @size="(newSize) => (bookModalSize = newSize)" @success="(commitData) => onBookingCreate(commitData)" @error="onBookingError"> </checkout>
            </b-modal>

            <b-modal ref="completeModal" title="¡Todo listo!" size="md" hide-footer no-fade no-close-on-backdrop no-close-on-esc hide-header-close no-enforce-focus centered scrollable>
                <h2 class="h5 mb-3">{{ multiSimBooking ? 'Reserva confirmada' : 'Simulador reservado correctamente' }}</h2>
                <reservation-racers-form v-if="multiSimBooking" ref="racersForm" :reservation-ids="lastCommitData.reservation_ids" :purchaser="userResource" />
                <p v-else class="mb-0 text-muted">Tu simulador está reservado.</p>

                <div class="d-flex justify-content-between align-items-center mt-4">
                    <b-button variant="link" class="px-0" @click="$router.push({ name: 'logout' })">Cerrar sesión</b-button>
                    <b-button variant="secondary" :disabled="completing" @click="completeBooking">
                        <b-spinner v-if="completing" small />
                        {{ multiSimBooking ? 'Enviar invitaciones y reservar de nuevo' : 'Reservar de nuevo' }}
                    </b-button>
                </div>
            </b-modal>

            <b-modal ref="errorModal" title="Error de reserva" ok-title="Revisar método de pago" cancel-title="Volver" @cancel="onBookAnother" @ok="$router.push({ name: 'dashboard' })" cancel-variant="primary" no-fade no-close-on-backdrop no-close-on-esc hide-header-close centered>
                <h3>Se produjo un error durante la reserva</h3>
                <b-alert variant="danger" show
                    >No se pudo completar tu reserva; hubo problemas al procesar tu tarjeta.<br />Detalles del error: <strong>{{ bookingErrorDetails }}</strong></b-alert
                >
            </b-modal>

            <b-modal ref="apexClubModal" size="md" hide-footer no-fade centered>
                <apex-club-pitch @success="$refs.apexClubModal.hide()" />
            </b-modal>

            <b-modal title="Selecciona una ubicación" v-model="locationModalVisible" scrollable hide-footer no-fade centered>
                <b-list-group>
                    <b-list-group-item v-for="(location, i) in locationResources" :key="i" @click="selectLocation(location)" href="javascript:{}">{{ location }}</b-list-group-item>
                </b-list-group>
            </b-modal>

            <waiver-modal ref="waiverModal" @success="onBookingCreate" />
        </b-container>

        <b-container class="mt-3 d-block d-sm-none pt-3" id="taco" v-if="selectedReservations.length > 0" fluid>
            <b-row>
                <b-col class="text-center text-muted">
                    <b-button variant="primary" @click="openReservationsModal()" class="col-sm-12">Finalizar compra</b-button>
                    <small>Se seleccionaron {{ selectedReservations.length === 1 ? '1 franja horaria' : `${selectedReservations.length} franjas horarias` }} con {{ distinctCalendarCount === 1 ? '1 simulador' : `${distinctCalendarCount} simuladores` }}</small>
                </b-col>
            </b-row>
        </b-container>
    </div>
</template>

<style lang="scss" scoped>
.calendars {
    overflow-x: scroll;

    .calendar {
        float: left;
        width: 260px;
    }
}

#taco {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 999;
    background-color: var(--panel);
    color: var(--ink);
    border-top: 1px solid var(--line-2);
    box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.28);
    padding-bottom: 1rem;
}
:root[data-theme='dark'] #taco {
    background-color: var(--panel-2);
    border-top-color: var(--line-2);
    box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.05);
}
</style>
